import { isScoredInteractionMode } from "@/lib/scenarios/interactionModes";
import { useCallback, useEffect, useRef, useState } from "react";
import DailyIframe, { type DailyCall } from "@daily-co/daily-js";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { tavus } from "@/lib/scenarios/providers/tavus/client";
import { postToAncestors } from "@/components/session/postToAncestors";
import { trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import type { Scenario, ScoreResult } from "@/lib/powtoon-be/types";
import { createLogger } from "@/lib/frontend-logger";
import { PreSessionScreen } from "@/components/session/PreSessionScreen";
import { SessionStartOverlay } from "@/components/session/SessionStartOverlay";
import { PostSessionScreen } from "@/components/session/PostSessionScreen";
import { WaitingForScoreScreen } from "@/components/session/WaitingForScoreScreen";
import { ScoreScreen } from "@/components/session/ScoreScreen";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { tavusSessionAdapter } from "@/lib/scenarios/providers/tavus/sessionAdapter";
import { AvatarVideoArea } from "@/components/session/AvatarVideoArea";
import { SessionControls } from "@/components/session/SessionControls";
import { DisplayDocument } from "@/components/session/DisplayDocument";
import {
  DraggableAvatarBubble,
  oppositeCorner,
  type BubbleCorner,
} from "@/components/session/DraggableAvatarBubble";
import { useBubbleSize } from "@/hooks/useBubbleSize";
import { withCacheBust } from "@/lib/utils";
import { UserCameraBubble } from "@/components/session/UserCameraBubble";
import { Eye } from "lucide-react";

const logger = createLogger("tavus.frame");
const END_FLOW_TIMEOUT_MS = 45_000;
const SCORE_FLOW_TIMEOUT_MS = 130_000;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise
      .then(resolve)
      .catch(reject)
      .finally(() => window.clearTimeout(timer));
  });
}

type PageState = "idle" | "connecting" | "connected" | "awaiting-score" | "scored" | "ended";

/**
 * Tavus session runtime. Creates a conversation via the `tavus` edge fn,
 * joins the returned Daily room headlessly with `@daily-co/daily-js`, and
 * ends the conversation on unmount. Fires the same lifecycle events as the
 * LiveAvatar frame so SCORM / LMS embeds keep working.
 */
export function TavusFrame({
  scenario,
  publicLinkToken,
}: {
  scenario: Scenario;
  publicLinkToken?: string;
}) {
  const content = scenario.content ?? {};
  const pal = content.pal;
  const auto_start = content.auto_start ?? false;
  const redirectUrl = content.redirect_url?.trim();
  const interactionMode = pal?.interaction_mode;
  const isScoredMode = isScoredInteractionMode(interactionMode);
  const display = content.display;
  const hasDisplay = !!display?.source;
  const displayLayout = display?.layout ?? "side-by-side";
  const avatarPosition: "left" | "right" = display?.avatar_position ?? "right";
  const cameraSharingDisabled = pal?.disable_camera_sharing === true;
  const screenSharingDisabled = pal?.disable_screen_sharing === true;

  const [pageState, setPageState] = useState<PageState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState<ScoreResult | null>(null);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localCameraStream, setLocalCameraStream] = useState<MediaStream | null>(null);
  const [localScreenStream, setLocalScreenStream] = useState<MediaStream | null>(null);
  const [remoteScreenStream, setRemoteScreenStream] = useState<MediaStream | null>(null);
  const [isSelfViewHidden, setIsSelfViewHidden] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const initialBubbleCorner: BubbleCorner = avatarPosition === "left" ? "tl" : "tr";
  const [bubbleCorner, setBubbleCorner] = useState<BubbleCorner>(initialBubbleCorner);
  const {
    containerRef: bubbleContainerRef,
    size: bubbleSize,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  } = useBubbleSize();
  const callRef = useRef<DailyCall | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const startedRef = useRef(false);
  const trackedRef = useRef(false);
  const cleanupPromiseRef = useRef<Promise<void> | null>(null);
  const pageStateRef = useRef<PageState>("idle");
  useEffect(() => {
    pageStateRef.current = pageState;
  }, [pageState]);

  const tracking = useSessionTracking({
    scenario,
    publicLinkToken,
    adapter: tavusSessionAdapter,
  });

  const ready = !!pal?.pal_id && !!pal?.face_id;

  const cleanup = useCallback(async () => {
    // Serialize teardown so a restart can await the in-flight cleanup instead
    // of racing it (Daily's instance counter is global).
    if (cleanupPromiseRef.current) return cleanupPromiseRef.current;
    const run = (async () => {
      const call = callRef.current;
      callRef.current = null;
      setRemoteStream(null);
      setLocalCameraStream(null);
      setLocalScreenStream(null);
      if (call) {
        try {
          await call.leave();
        } catch (e) {
          logger.warn("tavus call.leave failed", { error: String(e) });
        }
        try {
          await call.destroy();
        } catch (e) {
          logger.warn("tavus call.destroy failed", { error: String(e) });
        }
      }
      const cid = conversationIdRef.current;
      conversationIdRef.current = null;
      if (cid) {
        try {
          await tavus.endConversation(cid);
        } catch (e) {
          logger.warn("tavus endConversation failed", { error: String(e) });
        }
      }
    })();
    cleanupPromiseRef.current = run;
    try {
      await run;
    } finally {
      if (cleanupPromiseRef.current === run) cleanupPromiseRef.current = null;
    }
  }, []);

  // Ensure no Daily call object exists globally before creating a new one.
  const ensureNoLiveCall = useCallback(async () => {
    if (cleanupPromiseRef.current) {
      try {
        await cleanupPromiseRef.current;
      } catch {
        /* ignore */
      }
    }
    const stray = DailyIframe.getCallInstance();
    if (stray) {
      logger.warn("tavus destroying stray Daily call instance");
      try {
        await stray.leave();
      } catch (e) {
        logger.warn("tavus stray leave failed", { error: String(e) });
      }
      try {
        await stray.destroy();
      } catch (e) {
        logger.warn("tavus stray destroy failed", { error: String(e) });
      }
    }
  }, []);

  const failSession = useCallback(
    (message: string, meta?: Record<string, unknown>) => {
      logger.error("tavus session failed", {
        scenario_id: scenario.id,
        error: message,
        ...(meta ?? {}),
      });
      setError(message);
      setPageState("ended");
      startedRef.current = false;
      void cleanup();
    },
    [cleanup, scenario.id],
  );

  // Rebuild the remote replica MediaStream from Daily participants.
  const rebuildRemoteStream = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const participants = call.participants();
    const remote = Object.values(participants).find((p) => !p.local);
    if (!remote) {
      setRemoteStream(null);
      setRemoteScreenStream(null);
    } else {
      const vTrack = remote.tracks?.video?.persistentTrack ?? null;
      const aTrack = remote.tracks?.audio?.persistentTrack ?? null;
      if (!vTrack && !aTrack) {
        setRemoteStream(null);
      } else {
        setRemoteStream((prev) => {
          const existingIds = prev
            ? prev
                .getTracks()
                .map((t) => t.id)
                .sort()
                .join(",")
            : "";
          const nextTracks = [vTrack, aTrack].filter(Boolean) as MediaStreamTrack[];
          const nextIds = nextTracks
            .map((t) => t.id)
            .sort()
            .join(",");
          if (existingIds === nextIds && prev) return prev;
          return new MediaStream(nextTracks);
        });
      }

      // Presentation skill: the pal publishes slides as a `screenVideo` track
      // on its own participant. Daily reports `loading` before `playable`, so
      // anything that is not `off`/`blocked` counts as live.
      const remoteScreenTrack = remote.tracks?.screenVideo?.persistentTrack ?? null;
      const remoteScreenState = remote.tracks?.screenVideo?.state;
      const remoteScreenLive =
        remoteScreenTrack && remoteScreenState !== "off" && remoteScreenState !== "blocked";
      setRemoteScreenStream((prev) => {
        if (!remoteScreenLive) return null;
        const prevId = prev?.getVideoTracks()[0]?.id ?? "";
        if (prevId === remoteScreenTrack!.id && prev) return prev;
        return new MediaStream([remoteScreenTrack!]);
      });
    }

    // Local camera + screen streams for self-view / screen-share layout.
    const local = participants.local;
    const camTrack = local?.tracks?.video?.persistentTrack ?? null;
    const camState = local?.tracks?.video?.state;
    const camLive = camTrack && camState !== "off" && camState !== "blocked";
    setLocalCameraStream((prev) => {
      if (!camLive) return null;
      const prevId = prev?.getVideoTracks()[0]?.id ?? "";
      if (prevId === camTrack!.id && prev) return prev;
      return new MediaStream([camTrack!]);
    });
    const screenTrack = local?.tracks?.screenVideo?.persistentTrack ?? null;
    const screenState = local?.tracks?.screenVideo?.state;
    const screenLive = screenTrack && screenState !== "off" && screenState !== "blocked";
    setLocalScreenStream((prev) => {
      if (!screenLive) return null;
      const prevId = prev?.getVideoTracks()[0]?.id ?? "";
      if (prevId === screenTrack!.id && prev) return prev;
      return new MediaStream([screenTrack!]);
    });
  }, []);

  const startConversation = useCallback(
    async (languageOverride?: string) => {
      if (!ready || !pal) return;
      if (startedRef.current) return;
      startedRef.current = true;
      setError(null);
      setPageState("connecting");
      try {
        // Diagnostics: what knowledge the scenario believes the pal has, and
        // what Tavus reports right before the conversation starts.
        void (async () => {
          try {
            const entries = pal.knowledge_documents ?? [];
            logger.info("tavus session knowledge (scenario)", {
              scenario_id: scenario.id,
              pal_id: pal.pal_id,
              knowledge_document_id: pal.knowledge_document_id,
              presentation: pal.presentation,
              internet_search: pal.internet_search,
              documents: entries.map((e) => ({
                source_key: e.source_key,
                document_id: e.document_id,
                stored_status: e.status,
              })),
              system_prompt_length: pal.system_prompt?.length ?? 0,
              system_prompt_head: pal.system_prompt?.slice(0, 400),
            });
            const live = await Promise.all(
              entries.map(async (e) => {
                try {
                  const doc = await tavus.getDocument(e.document_id);
                  return {
                    document_id: e.document_id,
                    status: doc.status,
                    missing: doc.missing ?? false,
                    error_message: doc.error_message ?? null,
                  };
                } catch (err) {
                  return { document_id: e.document_id, error: String(err) };
                }
              }),
            );
            logger.info("tavus session knowledge (tavus)", {
              scenario_id: scenario.id,
              documents: live,
            });
            if (pal.pal_id) {
              try {
                const skill = await tavus.getPalSkill({
                  pal_id: pal.pal_id,
                  skill_id: "presentation",
                });
                logger.info("tavus session presentation skill", {
                  pal_id: pal.pal_id,
                  attached: skill.attached,
                  skill_document_ids: skill.config?.document_ids ?? null,
                  scenario_intent: pal.presentation?.enabled ?? false,
                });
              } catch (err) {
                logger.warn("tavus session presentation skill read failed", {
                  error: String(err),
                });
              }
              try {
                const search = await tavus.getPalSkill({
                  pal_id: pal.pal_id,
                  skill_id: "internet_search",
                });
                logger.info("tavus session internet_search skill", {
                  pal_id: pal.pal_id,
                  attached: search.attached,
                  scenario_intent: pal.internet_search?.enabled ?? false,
                });
              } catch (err) {
                logger.warn("tavus session internet_search skill read failed", {
                  error: String(err),
                });
              }
            }
          } catch (err) {
            logger.warn("tavus session knowledge diagnostics failed", { error: String(err) });
          }
        })();
        await ensureNoLiveCall();
        const res = await tavus.createConversation({
          pal_id: pal.pal_id!,
          face_id: pal.face_id!,
          custom_greeting: pal.opening_text || undefined,
          conversation_name: scenario.name,
          language: languageOverride || content.language || undefined,
        });
        conversationIdRef.current = res.conversation_id;
        let roomHost = "";
        try {
          roomHost = new URL(res.conversation_url).host;
        } catch {
          /* ignore */
        }
        logger.info("tavus conversation created", {
          conversation_id: res.conversation_id,
          status: res.status,
          scenario_id: scenario.id,
          room_host: roomHost,
        });
        const call = DailyIframe.createCallObject({
          subscribeToTracksAutomatically: true,
        });
        callRef.current = call;
        call.on("joining-meeting", () => {
          logger.info("tavus joining meeting", { conversation_id: res.conversation_id });
        });
        call.on("joined-meeting", () => {
          logger.info("tavus joined meeting", { conversation_id: res.conversation_id });
          setPageState("connected");
          tracking.markStarted();
          postToAncestors({
            event: "sessionStarted",
            sessionId: res.conversation_id,
            scenarioId: scenario.id,
          });
          trackEvent({
            category: ANALYTICS_CATEGORIES.SESSION,
            action: ANALYTICS_ACTIONS.STARTED,
            ga4EventName: "session_started",
            extraData: {
              scenario_id: scenario.id,
              session_id: res.conversation_id,
              provider: "tavus",
            },
          });
        });
        const onParticipantChange = () => rebuildRemoteStream();
        call.on("participant-joined", onParticipantChange);
        call.on("participant-updated", onParticipantChange);
        call.on("participant-left", onParticipantChange);
        call.on("track-started", onParticipantChange);
        call.on("track-stopped", onParticipantChange);
        call.on("local-screen-share-started", () => setIsScreenSharing(true));
        call.on("local-screen-share-stopped", () => setIsScreenSharing(false));
        call.on("left-meeting", () => {
          logger.info("tavus left meeting", { conversation_id: res.conversation_id });
          if (pageStateRef.current === "awaiting-score" || pageStateRef.current === "scored") {
            return;
          }
          setPageState("ended");
        });
        call.on("error", (ev: unknown) => {
          const msg = (ev as { errorMsg?: string })?.errorMsg || "Tavus call error";
          failSession(msg, { conversation_id: res.conversation_id, event: ev });
        });
        call.on("load-attempt-failed", (ev: unknown) => {
          const msg = (ev as { errorMsg?: string })?.errorMsg || "Failed to load Tavus room";
          failSession(msg, { conversation_id: res.conversation_id, event: ev });
        });
        call.on("nonfatal-error", (ev: unknown) => {
          logger.warn("tavus call nonfatal-error", {
            conversation_id: res.conversation_id,
            event: ev,
          });
        });
        try {
          await call.join({
            url: res.conversation_url,
            userName: "Learner",
            startVideoOff: true,
            startAudioOff: false,
          });
        } catch (joinErr) {
          const msg = (joinErr as Error).message || "Failed to join Tavus room";
          failSession(msg, { conversation_id: res.conversation_id });
        }
      } catch (e) {
        const msg = (e as Error).message || "Failed to start Tavus session";
        failSession(msg);
      }
    },
    [
      ready,
      pal,
      scenario.id,
      scenario.name,
      content.language,
      failSession,
      tracking,
      rebuildRemoteStream,
      ensureNoLiveCall,
    ],
  );

  const handleEnd = useCallback(async () => {
    const cid = conversationIdRef.current;
    setIsEnding(true);
    tracking.markEnded();
    trackEvent({
      category: ANALYTICS_CATEGORIES.SESSION,
      action: ANALYTICS_ACTIONS.COMPLETED,
      ga4EventName: "session_completed",
      extraData: { scenario_id: scenario.id, session_id: cid, provider: "tavus" },
    });
    if (isScoredMode) {
      setScore(null);
      setScoreError(null);
      pageStateRef.current = "awaiting-score";
      setPageState("awaiting-score");
    }
    logger.info("tavus end flow started", {
      scenario_id: scenario.id,
      session_id: cid,
      is_mcq: isScoredMode,
      questionnaire_id: pal?.questionnaire_ids?.[0] ?? null,
    });
    try {
      await withTimeout(
        cleanup(),
        END_FLOW_TIMEOUT_MS,
        "Timed out while ending Tavus conversation",
      );
      logger.info("tavus cleanup completed", { scenario_id: scenario.id, session_id: cid });
    } catch (e) {
      logger.warn("tavus cleanup timed out or failed", {
        scenario_id: scenario.id,
        session_id: cid,
        error: (e as Error)?.message ?? String(e),
      });
    }

    let trackResult: { transcriptObjectId: string | null } = {
      transcriptObjectId: null,
    };
    if (cid && !trackedRef.current) {
      trackedRef.current = true;
      try {
        trackResult = await withTimeout(
          tracking.trackOnEnd({ sessionId: cid }),
          END_FLOW_TIMEOUT_MS,
          "Timed out while saving Tavus session transcript",
        );
        logger.info("tavus tracking completed", {
          scenario_id: scenario.id,
          session_id: cid,
          transcript_object_id: trackResult.transcriptObjectId,
        });
      } catch (e) {
        const message =
          "We couldn't save your transcript in time, so your score can't be calculated right now. Please try again later or contact your trainer.";
        logger.error("tavus tracking failed", {
          scenario_id: scenario.id,
          session_id: cid,
          error: (e as Error)?.message ?? String(e),
        });
        if (isScoredMode) {
          setScoreError(message);
          pageStateRef.current = "scored";
          setPageState("scored");
        } else {
          setError(message);
          pageStateRef.current = "ended";
          setPageState("ended");
        }
        postToAncestors({ event: "sessionEnded", sessionId: cid, scenarioId: scenario.id });
        return;
      }
    }

    if (redirectUrl) {
      postToAncestors({ event: "sessionEnded", sessionId: cid, scenarioId: scenario.id });
      window.location.href = redirectUrl;
      return;
    }

    const transcriptObjectId = trackResult.transcriptObjectId;
    if (isScoredMode && transcriptObjectId) {
      logger.info("tavus scoring requested", {
        scenario_id: scenario.id,
        session_id: cid,
        transcript_object_id: transcriptObjectId,
        questionnaire_id: pal?.questionnaire_ids?.[0] ?? null,
      });
      let scoring: Awaited<ReturnType<typeof tracking.runScoringJob>>;
      try {
        scoring = await withTimeout(
          tracking.runScoringJob(transcriptObjectId),
          SCORE_FLOW_TIMEOUT_MS,
          "Timed out while waiting for Tavus score",
        );
      } catch (e) {
        logger.error("tavus scoring timed out or failed", {
          scenario_id: scenario.id,
          session_id: cid,
          transcript_object_id: transcriptObjectId,
          error: (e as Error)?.message ?? String(e),
        });
        scoring = {
          result: null,
          error:
            "We couldn't score your session right now. Please try again later or contact your trainer if the problem persists.",
          aborted: false,
        };
      }
      const { result, error: sErr, aborted } = scoring;
      if (aborted) return;
      if (result) {
        setScore(result);
        logger.info("tavus scoring completed", {
          scenario_id: scenario.id,
          session_id: cid,
          transcript_object_id: transcriptObjectId,
          pass: result.pass,
          grade: result.grade ?? null,
        });
        postToAncestors({
          event: "sessionScored",
          sessionId: cid,
          scenarioId: scenario.id,
          score: result,
        });
      } else if (sErr) {
        setScoreError(sErr);
      }
      setPageState("scored");
      postToAncestors({ event: "sessionEnded", sessionId: cid, scenarioId: scenario.id });
      return;
    }

    if (isScoredMode && !transcriptObjectId) {
      logger.error("tavus scoring skipped because transcript object id is missing", {
        scenario_id: scenario.id,
        session_id: cid,
      });
      setScoreError(
        "We saved your session but couldn't find the transcript object needed for scoring. Please try again later or contact your trainer.",
      );
      pageStateRef.current = "scored";
      setPageState("scored");
      postToAncestors({ event: "sessionEnded", sessionId: cid, scenarioId: scenario.id });
      return;
    }

    postToAncestors({ event: "sessionEnded", sessionId: cid, scenarioId: scenario.id });
    setPageState("ended");
  }, [cleanup, redirectUrl, scenario.id, isScoredMode, tracking]);

  const handleRestart = useCallback(async () => {
    // Wait for the previous call to be fully torn down before starting a new
    // one, otherwise Daily throws "Duplicate DailyIframe instances".
    await cleanup();
    startedRef.current = false;
    trackedRef.current = false;
    setScore(null);
    setScoreError(null);
    tracking.reset();
    setPageState("idle");
  }, [cleanup, tracking]);

  const handleBack = useCallback(() => {
    postToAncestors({ event: "sessionBack", scenarioId: scenario.id });
    if (typeof window !== "undefined" && window.parent === window) {
      try {
        window.history.back();
      } catch {
        /* ignore */
      }
    }
  }, [scenario.id]);

  useEffect(() => {
    return () => {
      void cleanup();
    };
  }, [cleanup]);

  // Best-effort persistence when the host removes the iframe or the tab
  // closes mid-session — mirrors the LiveAvatar unload beacon.
  useEffect(() => {
    const handler = () => {
      const st = pageStateRef.current;
      const live = st === "connecting" || st === "connected";
      if (!live || trackedRef.current) return;
      trackedRef.current = true;
      tracking.beaconOnUnload(conversationIdRef.current);
    };
    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, [tracking]);

  if (!ready) {
    return (
      <div className="h-screen w-screen grid place-items-center bg-background text-center px-6">
        <p className="text-sm text-muted-foreground">
          This Tavus scenario is missing a pal or face. Open the editor to fix it.
        </p>
      </div>
    );
  }

  if (pageState === "awaiting-score") {
    return <WaitingForScoreScreen scenario={scenario} />;
  }

  if (pageState === "scored") {
    return (
      <ScoreScreen scenario={scenario} score={score} error={scoreError} onRestart={handleRestart} />
    );
  }

  if (pageState === "ended") {
    return (
      <PostSessionScreen
        scenario={scenario}
        subtitle={error ?? undefined}
        onRestart={handleRestart}
      />
    );
  }

  // Teardown / transcript / scoring window: show the post-session screen with
  // restart disabled instead of falling through to the start screen.
  if (isEnding) {
    return <PostSessionScreen scenario={scenario} onRestart={handleRestart} busy />;
  }

  // The Daily container stays in the DOM so `createFrame()` can attach to it.
  // `PreSessionScreen` only covers the initial choice and create-conversation
  // request; after a Daily URL exists, the iframe owns its own loading UI.
  const hasVideo =
    !!remoteStream && remoteStream.getVideoTracks().some((t) => t.readyState === "live");
  const isLive = pageState === "connected" && hasVideo && !isEnding;
  // "Joined the room but no video frame yet" is still starting up: keep the
  // loading state so the interactive pre-session screen never flashes back.
  const isConnecting =
    pageState === "connecting" || (pageState === "connected" && !hasVideo && !isEnding);
  const showSelfView = isCameraOn && !!localCameraStream && !isSelfViewHidden;
  const showRestoreSelfView = isCameraOn && !!localCameraStream && isSelfViewHidden;

  const commonControlsProps = {
    isMuted,
    onToggleMute: async () => {
      const next = !isMuted;
      try {
        await callRef.current?.setLocalAudio(!next);
        setIsMuted(next);
      } catch (err) {
        logger.warn("tavus setLocalAudio failed", { error: err });
      }
    },
    isCameraOn,
    onToggleCamera: cameraSharingDisabled
      ? undefined
      : async () => {
          const next = !isCameraOn;
          try {
            await callRef.current?.setLocalVideo(next);
            setIsCameraOn(next);
            if (!next) setIsSelfViewHidden(false);
          } catch (err) {
            logger.warn("tavus setLocalVideo failed", { error: err });
          }
        },
    isScreenSharing,
    onToggleScreenShare: screenSharingDisabled
      ? undefined
      : async () => {
          const call = callRef.current;
          if (!call) return;
          try {
            if (isScreenSharing) {
              call.stopScreenShare();
            } else {
              // Note: Chromium always focuses the picked tab in the
              // getDisplayMedia picker — no supported option suppresses that.
              // Users who want to avoid the focus jump can pick Window or
              // Entire screen instead.
              await call.startScreenShare();
            }
          } catch (err) {
            logger.warn("tavus screen share failed", { error: err });
            setIsScreenSharing(false);
          }
        },
    isSelfViewHidden,
    onToggleSelfView: cameraSharingDisabled ? undefined : () => setIsSelfViewHidden((v) => !v),
    onEnd: handleEnd,
  };

  return (
    <div className="relative flex h-[100dvh] w-screen bg-background">
      {!isConnecting && !isEnding && !isLive && (
        <Button
          onClick={handleBack}
          size="icon"
          variant="secondary"
          className="absolute top-4 left-4 z-20 rounded-lg bg-card/70 backdrop-blur"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      )}
      {isLive ? (
        <div className="relative flex-1 min-h-0 flex flex-col">
          {(() => {
            // The "content" slot holds whatever occupies the primary pane
            // alongside (or behind) the avatar. Screen share takes priority
            // over any configured document — when the learner stops sharing,
            // the document returns.
            const sharedScreenNode =
              isScreenSharing && localScreenStream ? (
                <div className="h-full w-full bg-black relative overflow-hidden">
                  <video
                    autoPlay
                    playsInline
                    muted
                    ref={(el) => {
                      if (el && el.srcObject !== localScreenStream) {
                        el.srcObject = localScreenStream;
                        el.play().catch(() => {});
                      }
                    }}
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                </div>
              ) : null;
            const presentationNode = remoteScreenStream ? (
              <div className="h-full w-full bg-black relative overflow-hidden">
                <video
                  autoPlay
                  playsInline
                  muted
                  ref={(el) => {
                    if (el && el.srcObject !== remoteScreenStream) {
                      el.srcObject = remoteScreenStream;
                      el.play().catch(() => {});
                    }
                  }}
                  className="absolute inset-0 h-full w-full object-contain"
                />
              </div>
            ) : null;
            const documentNode =
              hasDisplay && display?.source ? (
                <DisplayDocument
                  scenarioId={scenario.id}
                  source={display.source}
                  publicLinkToken={publicLinkToken}
                />
              ) : null;
            const contentNode = sharedScreenNode ?? presentationNode ?? documentNode;
            const hasContent = !!contentNode;

            // Effective layout: screen share on a bubble-configured scenario
            // keeps the bubble avatar; everything else with content uses
            // side-by-side. No content → full-screen avatar.
            const useBubbleLayout = hasContent && displayLayout === "bubble" && hasDisplay;

            if (!hasContent) {
              return (
                <div className="flex-1 min-h-0 relative">
                  <AvatarVideoArea
                    previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
                    mediaStream={remoteStream}
                    {...commonControlsProps}
                    hideControls
                  />
                  {showSelfView && (
                    <UserCameraBubble
                      stream={localCameraStream}
                      onHide={() => setIsSelfViewHidden(true)}
                      initialCorner="bl"
                    />
                  )}
                </div>
              );
            }

            if (useBubbleLayout) {
              // Content is background (document or shared screen); avatar in
              // a draggable/resizable bubble. If the learner has camera on,
              // put the user-camera bubble in the opposite corner.
              const userBubbleCorner = oppositeCorner(bubbleCorner);
              return (
                <div ref={bubbleContainerRef} className="flex-1 min-h-0 relative">
                  {contentNode}
                  <DraggableAvatarBubble
                    initialCorner={initialBubbleCorner}
                    size={bubbleSize}
                    onResizeStart={onResizeStart}
                    onResizeMove={onResizeMove}
                    onResizeEnd={onResizeEnd}
                    onCornerChange={setBubbleCorner}
                  >
                    <AvatarVideoArea
                      previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
                      mediaStream={remoteStream}
                      {...commonControlsProps}
                      hideControls
                    />
                  </DraggableAvatarBubble>
                  {showSelfView && (
                    <UserCameraBubble
                      stream={localCameraStream}
                      onHide={() => setIsSelfViewHidden(true)}
                      initialCorner={userBubbleCorner}
                    />
                  )}
                </div>
              );
            }

            // Side-by-side: content pane 2/3, avatar column 1/3.
            // When the camera is on, the user bubble floats inside the
            // content pane (bl corner) so both avatar + self view are
            // visible without stealing avatar space.
            const reverse = avatarPosition === "left";
            return (
              <div
                className={
                  "flex-1 min-h-0 flex flex-col lg:flex-row " +
                  (reverse ? "lg:flex-row-reverse" : "")
                }
              >
                <div
                  className={
                    "flex-[2] min-h-0 lg:flex-none lg:w-2/3 lg:h-full relative " +
                    (reverse
                      ? "border-b lg:border-b-0 lg:border-l border-border"
                      : "border-b lg:border-b-0 lg:border-r border-border")
                  }
                >
                  {contentNode}
                  {showSelfView && (
                    <UserCameraBubble
                      stream={localCameraStream}
                      onHide={() => setIsSelfViewHidden(true)}
                      initialCorner="bl"
                    />
                  )}
                </div>
                <div className="flex-1 min-h-0 relative lg:flex-none lg:w-1/3 lg:h-full">
                  <AvatarVideoArea
                    previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
                    mediaStream={remoteStream}
                    {...commonControlsProps}
                    hideControls
                  />
                </div>
              </div>
            );
          })()}
          <SessionControls
            {...commonControlsProps}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30"
          />
          {showRestoreSelfView && (
            <Button
              onClick={() => setIsSelfViewHidden(false)}
              size="sm"
              variant="secondary"
              className="absolute top-4 left-4 z-20 rounded-full bg-card/80 backdrop-blur shadow-lg"
              aria-label="Show self view"
            >
              <Eye className="h-4 w-4" />
              Self view
            </Button>
          )}
        </div>
      ) : auto_start ? (
        <div className="absolute inset-0 z-10">
          <SessionStartOverlay
            previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
            busy={isConnecting}
            onStart={() => void startConversation()}
            errorMessage={error}
          />
        </div>
      ) : (
        <div className="absolute inset-0 z-10">
          <PreSessionScreen
            scenario={scenario}
            connecting={isConnecting}
            onStart={(lang) => void startConversation(lang)}
            onBack={handleBack}
          />
        </div>
      )}
    </div>
  );
}
